import { ChangeEvent, FC, useState } from 'react';
import Breadcrumbs from '../../components/Breadcrumb';
import { DEBOUNCE_TIME } from '../../configs';
import { UserRole } from '../../types/user.types';
import { metaTitle } from '../../util/tabTitle';
import { useIsUserRole } from '../../util/useIsUserRole';
import { AddFormModal } from './AddFormModal';
import FormsTable from './FormsTable';
import { Box, debounce, Stack } from '@mui/material';
import { useHandleSearch } from '../../util/useHandleSearch';
import { useLocationSearch } from '../../util/useLocationSearch';
import {
  ManagementPageHeader,
  ManagementSearch,
  ManagementToolbar,
  RefreshAction,
} from '../../components/ManagementPageLayout';

const FormsPage: FC = () => {
  metaTitle('Forms');

  const searchVal = useLocationSearch();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(searchVal || '');
  const [searchText, setSearchText] = useState(searchVal || '');
  const [refreshTable, setRefreshTable] = useState(false);
  useHandleSearch(searchText);

  const isViewOnly = useIsUserRole([UserRole.user, UserRole.dataEditor]);

  const handleSearch = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSearch(event.target.value);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto' }}>
      <Breadcrumbs
        items={[
          {
            label: 'Forms',
            to: '/forms',
            description:
              'This page serves as a platform for creating and managing feedback forms. The main interface provides an overview of existing forms, allowing users to edit form content, rename form titles, and delete unused forms. Once a form has been utilized, clicking on its name enables users to access a summary of all the responses received for that specific form. This feature allows users to review and analyze the collected feedback effectively.',
          },
        ]}
      />

      <ManagementPageHeader
        title="Forms"
        subtitle="Create feedback forms and review collected responses."
        actionLabel="New form"
        onAction={() => setOpen(true)}
        actionDisabled={isViewOnly}
      />
      <ManagementToolbar>
        <ManagementSearch
          placeholder="Search forms"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            debounce(() => handleSearch(event), DEBOUNCE_TIME)();
          }}
        />
        <Stack direction="row" justifyContent="flex-end">
          <RefreshAction onClick={() => setRefreshTable(!refreshTable)} />
        </Stack>
      </ManagementToolbar>

      <FormsTable refresh={refreshTable} search={search} />
      <AddFormModal onClose={() => setOpen(false)} open={open} />
    </Box>
  );
};

export default FormsPage;
